import type { BasicCoveragesProps } from '@/components/car/BasicCoverages';
import type { DrivingProps } from '@/components/car/Driving';
import type { FaqProps } from '@/components/car/Faq';
import type { LowMilagePpmProps } from '@/components/car/LowMilagePpm';
import type { OverallCoveragesProps } from '@/components/car/OverallCoverages';
import {
  hitPpmImg,
  liabilityPpmImg,
  roadsideAssistancePpmImg,
  roadsideAssistancePpmImg2,
  theftPpmtImg,
  unexpectedPpmImg,
} from '@/components/car/assets/coverages/basic';
import {
  claimsPpmImg,
  emergencyPpmImg,
  fairPricesPpmImg,
  mechanicalAlertsPpmImg,
  parkingNotificationsPpmImg,
  theftRecoveryPpmImg,
} from '@/components/car/assets/coverages/overall';
import type { CustomersReviewsProps } from '@/components/shared';
import { sharedConfig } from '@/pageConfig/shared';
import ogImage from '@/static/assets/car/og.png';
import type { BasePageConfig } from '../../../BasePageConfig';

export interface Config extends BasePageConfig {
  hero: DrivingProps;
  overallCoverages: OverallCoveragesProps;
  lowMilagePpm: LowMilagePpmProps;
  basicCoverages: BasicCoveragesProps;
  customersReviews: CustomersReviewsProps;
  faq: FaqProps;
}

export const config: Config = {
  seo: {
    title: 'Pay Per Mile Car Insurance • Lemonade',
    metaTitle: 'Pay Per Mile Car Insurance • Lemonade',
    description: 'Pay just as much as you drive - With Pay-per-mile car insurance, you could save a lot every year',
    ogImage: ogImage.src,
    advancedRobots: true,
    noIndex: true,
    uniqueCanonicalUrl: 'https://www.lemonade.com/car/pay-per-mile',
  },
  layout: { ...sharedConfig['en-US'].layout },
  hero: {
    title: 'Insurance that costs less<br/>when you drive less',
    subtitle: 'Super fast everything. Seamless claims. Built on 3 billion miles of data.',
  },
  overallCoverages: {
    priceType: 'ppm',
    title: 'A new kind of car insurance',
    subtitle: 'We use a smart device to make your insurance better and more affordable.',
    coverages: [
      {
        background: claimsPpmImg.src,
        title: 'Super fast claims resolution',
        description: 'We’ll handle your claim quickly with the extra details we have.',
        alt: 'Instant car insurance quotes online',
      },
      {
        background: fairPricesPpmImg.src,
        title: 'Fair prices, based exactly on how you drive',
        description: 'Our device let’s us track your mileage down to a single foot.',
        alt: 'auto insurance discounts',
      },
      {
        background: emergencyPpmImg.src,
        title: 'Emergency crash assistance',
        description: 'We’ll know if you’re in an accident and get you the help you need.',
        alt: 'car accident assistance and coverage with your car insurance',
        pill: {
          text: 'free',
        },
      },
      {
        background: parkingNotificationsPpmImg.src,
        title: 'Parking notifications',
        description: 'We’ll keep track of your spot and send you street sweeping reminders.',
        alt: 'parking notifications',
        pill: {
          text: 'soon',
          backgroundColor: 'pebble',
        },
      },
      {
        background: theftRecoveryPpmImg.src,
        title: 'Theft recovery',
        description: 'If your car is stolen, our device will help us locate it and return it to you.',
        alt: 'Theft recovery',
      },
      {
        background: mechanicalAlertsPpmImg.src,
        title: 'Mechanical alerts',
        description: 'From tire pressure to engine malfunctions, we’ll keep you in the know.',
        alt: 'mechanical alerts',
        pill: {
          text: 'soon',
          backgroundColor: 'pebble',
        },
      },
    ],
  },
  lowMilagePpm: {
    title: 'Driving less than you used to?',
    subtitle:
      'Since your monthly mileage determines your premium,\nif you’re not driving, you won’t pay more than your low base price',
  },
  basicCoverages: {
    title: 'What’s covered',
    subtitle: 'Whether it’s a fender bender or total loss, our team and app are ready to help!',
    darkBackground: false,
    coverages: [
      {
        background: hitPpmImg.src,
        title: 'If you’re in a<br/>car crash',
        description: "We'll cover, or recover, the costs to repair the damage.",
        alt: 'car crash insurance',
      },
      {
        background: roadsideAssistancePpmImg.src,
        title: 'If you’re stalled on the highway',
        description: 'We include roadside assistance for tows, jumps, and more.',
        alt: 'flat tire, towing and jumps insurance coverage',
      },
      {
        background: unexpectedPpmImg.src,
        title: 'If the unexpected\nhappens',
        description: 'We’ll cover damage from fires, weather, vandalism, and more.',
        alt: 'Car damage insurance',
      },
      {
        background: roadsideAssistancePpmImg2.src,
        title: 'Roadside assistance',
        description: 'Get tows, jump starts, tire changes, and winching.',
        alt: 'windshield and sunroof repairs coverage',
      },
      {
        background: theftPpmtImg.src,
        title: 'If your car<br/>is stolen',
        description: "We'll cover the cost to replace it with a new car.",
        alt: 'stolen car insurance',
      },
      {
        background: liabilityPpmImg.src,
        title: 'If you’re sued<br/>for liability',
        description: 'We’ll help cover the costs of a lawyer to defend you.',
        alt: 'car liability insurance for lawyer expenses',
      },
    ],
  },
  customersReviews: {
    title: 'America’s top-rated insurance,\nnow for your car',
    subtitle: '4.9 stars in the App store, and top-rated by Supermoney, Clearsurance, and others',
    darkBackground: true,
  },
  faq: {
    title: 'Good to know...',
    subtitle: `Car insurance can be confusing, so we worked hard to make it simple. If you\nstill have questions, you can check <a target="_blank" rel="noreferrer" href="https://www.lemonade.com/car/explained/">car insurance explained</a>`,
    data: () => [
      {
        question: 'How do you help me clean up CO₂?',
        answer:
          'By planting trees! We learned that a typical car emits 4.6 metric tons of CO₂ each year, which is a lot. So we decided we’ll plant trees to help shrink your carbon footprint, using our app to determine the number of trees based on your mileage.',
        analytics: 'car_clean_co2',
      },

      {
        question: 'Can I trust you guys?',
        answer:
          'Of course. Unlike most of the new insurance startups out there, Lemonade is an insurance carrier, reinsured by some of the most trusted names in the industry. We’re a public company, traded on the NYSE, and rated A-Exceptional for financial stability.',
        analytics: 'car_fixed_trust',
      },
      {
        question: 'Do all drivers need the Lemonade app on their phone?',
        answer:
          'Depending on the state, they do. We use smart technology to price policies fairly, speed up claims, provide on-site emergency services, and measure driving impact—like CO₂ emissions so we can plant trees in your name.',
        analytics: 'car_fixed_app',
      },
      {
        question: 'Is it okay to disable location services for the Lemonade app?',
        answer:
          'In some states, not using the app can affect your price. Keep location on for the best rate, access to emergency services, a smoother claims experience, and more.',
        analytics: 'car_fixed_location_services',
      },
      {
        question: 'Can I switch now if I’m currently insured elsewhere?',
        answer:
          'Sure. You’ll need to send a cancellation request to your current carrier after you sign up for a Lemonade policy to make sure there’s no gaps in your coverage. Our team will be happy to help!',
        analytics: 'car_fixed_switch_now',
      },
      {
        question: 'What happens if I’m in an accident?',
        answer:
          'Once everyone’s safe, you’ll need to file a claim through our app. It takes a couple of minutes and is super simple- just answer a few questions, and follow the steps. We’ll reimburse you for repairs or replace your car on covered claims.',
        analytics: 'car_fixed_accident',
      },
      {
        question: 'I’m an Uber/Lyft driver, can I use your insurance?',
        answer:
          'Not yet. Our current coverage only works for drivers who don’t use their cars for business or commercial purposes. Sorry!',
        analytics: 'car_fixed_ridesharing',
      },
      {
        question: 'I’m buying a new car, how do I insure it?',
        answer:
          'Once you have a Lemonade Car policy it’s really easy to add a new car in the app or on our site. If you’re buying a new car when you open a policy we can provide same-day coverage - our team will be happy to help!',
        analytics: 'car_fixed_new_car',
      },
      {
        question: 'Can I edit my coverage after purchase?',
        answer:
          'No problem. You can edit your coverages by adjusting your limits and deductibles in the app or on our site whenever you’d like.',
        analytics: 'car_fixed_coverage',
      },
      {
        question: 'What happens if someone who’s not insured hits my car?',
        answer:
          'You’re covered. Most states require protection that guarantees you’re not left in a tough spot if an uninsured driver causes an accident, so your Lemonade Car policy will cover the costs of these damages in a covered claim.',
        analytics: 'car_fixed_uninsured_driver',
      },
      {
        question: 'How do you calculate my price?',
        answer:
          'We use a number of factors to calculate the most accurate and fair price, including your car’s model, as well as your location and driving history. In other words: Safe, low mileage drivers pay less.',
        analytics: 'car_fixed_price_calculation',
      },
      {
        question: 'What discounts do you have for eco-friendly cars?',
        answer:
          'We offer a discount for all electric and hybrid cars, plus we’ll cover you for emergency charging and damage to your wall charger.',
        analytics: 'car_fixed_eco_friendly_discounts',
      },
    ],
  },
};
